import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { map } from 'rxjs';

import { CaliperFactoryService } from '../utils/caliper-factory.service';
import { ChartViewModel, MeasuresSummaryViewModel, MeasuresViewModel } from '../view-models/chart-viewmodel';
import { ChannelType } from '../models/ichannel';

import { EcgStore } from '../redux/providers';
import { IAction, Store, UnsubscribeCallback } from '@se/se.ui.advanced';
import { CaliperMessage, EcgState } from '../redux/ecgstate';
import {
    ARRANGEMENT,
    BASELINE,
    CALIPER_ADD,
    CALIPER_MODIFY,
    CALIPER_REMOVE,
    FIT,
    MessageActions,
    PAPER_RATE,
    SUMMARY_VISIBLE,
} from '../redux/actions';

import { TraceViewModel } from '../view-models/trace-viewmodel';
import { UniqueNameService } from '../utils/unique-name.service';
import { Zoomer } from '../utils/zoomer';
import { EcgMeasuresService, ICalculationContext } from '../utils/ecg-measures.service';

import { Layout } from '../layout/layout';
import { ArrangementType, CaliperMode } from '../models/emuns';

@Component({
    selector: 'ecg-chart',
    templateUrl: './ecg-chart.component.html',
    styleUrls: ['./ecg-chart.component.css'],
    viewProviders: [Layout, Zoomer],
    encapsulation: ViewEncapsulation.None,
})
export class EcgChartComponent implements ICalculationContext, OnDestroy {
    // events
    @Output()
    fitChange = new EventEmitter<any>();
    @Output()
    caliperActivated = new EventEmitter<boolean>();

    // data members

    @Input()
    title: string = '';

    @Input()
    hideWhileCentering: boolean = true;

    actualWidth: number = 0;
    actualHeight: number = 0;
    translate: any;
    measuresSummary = new MeasuresSummaryViewModel();

    arrangement$ = this.store.select('chartState').pipe(map((s) => s.arrangement));

    private lastCaliperMode = CaliperMode.single;

    private storeSubscription!: UnsubscribeCallback;

    // constructor

    constructor(
        @Inject(EcgStore) private store: Store<EcgState>,
        public layout: Layout,
        public element: ElementRef,
        private caliperFactoryService: CaliperFactoryService,
        private ecgMeasuresService: EcgMeasuresService,
        private zoomer: Zoomer,
        private uniqueNameService: UniqueNameService,
    ) {
        this.listenForEvents();
    }

    ngOnDestroy() {
        this.storeSubscription();
        this.zoomer.cleanup();
    }

    // ICalculationContext interface

    get leadWidth(): number {
        return this.layout.context?.leadWidth || 0;
    }

    get leadHeight(): number {
        return this.layout.context?.leadHeight || 0;
    }

    get columns(): number {
        return this.layout.columns;
    }

    get period(): number {
        return this.layout.ecgState.chartState.arrangement == ArrangementType.Medians ? 1 : this.layout.columns;
    }

    // properties

    private _chart!: ChartViewModel;
    get chart() {
        return this._chart;
    }

    @Input()
    set chart(chart: ChartViewModel) {
        this.updateChart(chart);
    }

    // public functions

    updateState() {
        this.layout.ecgState = this.store.getState();
        this.zoomer.factor = this.store.getState().chartState.zoomFactor;
    }

    rescale() {
        this.zoomer.rescale();
    }

    fit() {
        this.fitChange.emit();
    }

    clearAllMeasures() {
        this.clearCaliperMeasures();
        this.updateMeasuresSummaryVisibility();
    }

    onMeasuresRemove(measures: MeasuresViewModel) {
        let currentTrace: TraceViewModel | null = null;

        this.chart.layers.forEach((layer) =>
            layer.traces.forEach((trace) => {
                trace.calipers.forEach((caliper) => {
                    if (caliper == measures.caliper) currentTrace = trace;
                });
            }),
        );

        if (currentTrace) {
            this.caliperRemove(new CaliperMessage(currentTrace, measures.caliper));
            this.updateMeasuresSummaryVisibility();
        }
    }

    // private functions

    private updateChart(chart: ChartViewModel) {
        if (chart == null) return;

        this._chart = chart;

        this.layout.init(this.chart);

        this.zoomer.cleanup();
        this.zoomer.init(this.element, this.chart);
    }

    enableZoomOnWheel() {
        this.zoomer.enableZoomOnWheel();
    }

    disableZoomOnWheel() {
        this.zoomer.disableZoomOnWheel();
    }

    private listenForEvents() {
        this.storeSubscription = this.store.subscribe((action: IAction) => {
            this.handleStoreActions(action);
        });

        this.zoomer.zoomFactorChange.subscribe((factor) => {
            this.store.dispatch(MessageActions.zoomFactorMessage(factor));
        });
        this.zoomer.cyclicFitFired.subscribe(() => {
            this.fit();
        });
    }

    private clearCaliperMeasures() {
        this.chart.layers.forEach((layer) =>
            layer.traces.forEach((trace) => {
                trace.calipers.splice(0, trace.calipers.length);
            }),
        );

        this.measuresSummary.measuresHandle.clear();
        this.caliperActivated.emit(false);
    }

    private handleStoreActions(action: IAction) {
        if (this.chart == null || this.chart.layers == null) return;

        switch (action.type) {
            case BASELINE:
                this.chart.layers.forEach((layer) =>
                    layer.traces.forEach((trace) => {
                        let offset = trace.channel?.samples[0].value;

                        // SAMPLES
                        trace.channel?.samples.forEach((sample) => {
                            sample.value = sample.value - (offset || 0);
                        });

                        trace.updateTrace();
                    }),
                );
                break;
            case ARRANGEMENT:
            case PAPER_RATE:
            case SUMMARY_VISIBLE:
                this.clearCaliperMeasures();
                this.updateMeasuresSummaryVisibility(true);
                break;
            case FIT:
                this.fit();
                break;
            case CALIPER_ADD:
            case CALIPER_REMOVE:
            case CALIPER_MODIFY:
                this.handleCaliperMessages(action.payload, action.type);
                break;
        }
    }

    private handleCaliperMessages(message: CaliperMessage, caliperActionType: string) {
        if (this.lastCaliperMode != this.store.getState().chartState.caliperMode) {
            this.clearCaliperMeasures();
            this.lastCaliperMode = this.store.getState().chartState.caliperMode;
        }

        switch (caliperActionType) {
            case CALIPER_ADD:
                this.caliperAdd(message);
                break;
            case CALIPER_REMOVE:
                this.caliperRemove(message);
                break;
            case CALIPER_MODIFY:
                this.caliperModify(message);
                break;
        }

        this.updateMeasuresSummaryVisibility();
    }

    private caliperAdd(message: CaliperMessage) {
        if (this.store.getState().chartState.caliperMode == CaliperMode.multi) {
            this.clearCaliperMeasures();

            this.chart.layers.forEach((layer) =>
                layer.visibleTraces.forEach((trace) => {
                    let caliper = this.caliperFactoryService.create(trace, { x: message.caliper.getPositionX(), y: 0 });
                    if (caliper) {
                        trace.calipers.push(caliper);
                        caliper.title = this.uniqueNameService.generate(
                            trace.calipers,
                            caliper,
                            ChannelType[trace.channel?.type || ChannelType.UNKNOWN].replace('LEAD_', ''),
                        );
                        this.pushMeasure(new MeasuresViewModel(caliper));
                    }
                }),
            );
        } else {
            message.trace.calipers.push(message.caliper);
            message.caliper.title = this.uniqueNameService.generate(
                message.trace.calipers,
                message.caliper,
                ChannelType[message.trace.channel?.type || ChannelType.UNKNOWN].replace('LEAD_', ''),
            );

            this.pushMeasure(new MeasuresViewModel(message.caliper));
        }
    }

    private pushMeasure(measure: MeasuresViewModel) {
        setTimeout(() => {
            this.measuresSummary.measuresHandle.measuresList.push(measure);
            if (measure.caliper.measures)
                this.measuresSummary.measuresHandle.update(measure.caliper, measure.caliper.measures);
            if (this.measuresSummary.measuresHandle.count === 1) this.caliperActivated.emit(true);

            this.store.dispatch(MessageActions.measureListChangedMessage());
        });
    }

    private caliperRemove(message: CaliperMessage) {
        message.trace.calipers.splice(message.trace.calipers.indexOf(message.caliper), 1);

        if (this.store.getState().chartState.caliperMode == CaliperMode.multi) this.clearCaliperMeasures();
        else {
            this.measuresSummary.measuresHandle.remove(message.caliper);
            if (this.measuresSummary.measuresHandle.count === 0) this.caliperActivated.emit(false);
        }
    }

    private caliperModify(message: CaliperMessage) {
        if (this.store.getState().chartState.caliperMode == CaliperMode.multi) {
            this.chart.layers.forEach((layer) =>
                layer.visibleTraces.forEach((trace) => {
                    if (message.caliper != trace.calipers[0] && trace.calipers.length == 1) {
                        trace.calipers[0].updatePosition(
                            message.caliper.getPositionX(),
                            Math.abs(message.caliper.pointer2.positionX - message.caliper.pointer1.positionX),
                            message.caliper.pointer1.positionX,
                            message.caliper.pointer2.positionX,
                        );
                        let measures = this.ecgMeasuresService.calculate(trace.calipers[0], this.layout);
                        trace.calipers[0].updateMeasures(measures);
                    }

                    if (trace.calipers[0].measures)
                        this.measuresSummary.measuresHandle.update(trace.calipers[0], trace.calipers[0].measures);
                }),
            );
        } else if (message.caliper.measures)
            this.measuresSummary.measuresHandle.update(message.caliper, message.caliper.measures);

        this.measuresSummary.measuresHandle.select(message.caliper);
    }

    private updateMeasuresSummaryVisibility(forceFit: boolean = false) {
        let visible = this.measuresSummary.visible;

        this.measuresSummary.visible = !this.chart.layers.every((layer) =>
            layer.traces.every((trace) => trace.calipers.length == 0),
        );

        if (this.measuresSummary.visible != visible || forceFit) this.fit();
    }
}
